import { Route } from '@/routes/pantry/route.jsx';
import { hooks } from '@/stores/groceries/index.js';
import { shortenTimeUnits } from '@a-type/utils';
import { useLocalStorage } from '@biscuits/client';
import { Food } from '@gnocchi.biscuits/verdant';
import { addDays } from 'date-fns/addDays';
import { endOfDay } from 'date-fns/endOfDay';
import { formatDistanceToNowStrict } from 'date-fns/formatDistanceToNowStrict';
import { useCallback } from 'react';

// keeping this static to make the query reusable
export const THREE_DAYS_FROM_NOW = addDays(endOfDay(new Date()), 3).getTime();
export function useExpiresSoonItems({
	skip,
	key,
	limit,
}: { skip?: boolean; key?: string; limit?: number } = {}) {
	return hooks.useAllFoodsUnsuspended({
		index: {
			where: 'purchasedAndExpiresAt',
			lt: THREE_DAYS_FROM_NOW,
		},
		skip,
		limit,
		key: key ?? 'expiresSoonItems',
	}).data;
}

// something weird is going on in this hook, or the
// way it's being called... I have to store this in
// module scope to keep it alive.
let newerExpireTime: number | undefined = undefined;
export function useHasNewExpirations() {
	const [latestSeen, setLatestSeen] = useLocalStorage(
		'latestSeenExpiration',
		// eslint-disable-next-line react-hooks/purity
		Date.now(),
	);
	const expiresSoonItems = useExpiresSoonItems();
	const latestExpiration = expiresSoonItems.reduce((latest, item) => {
		const expiresAt = item.get('expiresAt')!;
		if (expiresAt > latest) return expiresAt;
		return latest;
	}, 0);
	// eslint-disable-next-line react-hooks/globals
	newerExpireTime =
		latestExpiration > latestSeen ? latestExpiration : undefined;
	const onSeen = () => {
		if (newerExpireTime) {
			setLatestSeen(newerExpireTime);
		}
	};
	return [!!newerExpireTime, onSeen] as const;
}

export function useExpiresText(food: Food | null, abbreviate = false) {
	hooks.useWatch(food);
	const expiresAt = food?.get('expiresAt');
	if (!expiresAt) return '';
	const inThePast = expiresAt < Date.now();
	const toNow = formatDistanceToNowStrict(expiresAt, { addSuffix: true });
	return `${inThePast ? 'Expired' : 'Expires'} ${
		abbreviate ? shortenTimeUnits(toNow) : toNow
	}`;
}

export function usePurchasedText(food: Food | null, abbreviate = false) {
	hooks.useWatch(food);
	const purchasedAt = food?.get('lastPurchasedAt');
	if (!purchasedAt) return '';
	const toNow = formatDistanceToNowStrict(purchasedAt, { addSuffix: true });
	return `Purchased ${abbreviate ? shortenTimeUnits(toNow) : toNow}`;
}

/**
 * Uses a query parameter to decide whether to show all
 * items or only purchased items. Defaults to purchased.
 */
export function useFilter() {
	const { filter = 'purchased' } = Route.useSearch();
	const navigate = Route.useNavigate();
	const setFilter = (filter: 'purchased' | 'all' | 'frozen') => {
		navigate({
			search: {
				filter,
			},
		});
	};
	return [filter, setFilter] as const;
}

export function useSearch() {
	const navigate = Route.useNavigate();
	const { query = '' } = Route.useSearch();
	const setSearch = useCallback(
		(search: string) => {
			if (!search) {
				navigate({
					search: {
						query: undefined,
					},
				});
				return;
			} else {
				navigate({
					search: {
						query: search.toLowerCase(),
					},
				});
			}
		},
		[navigate],
	);
	return [query.toLowerCase(), setSearch] as const;
}
